import { makeParquetQuerier } from "./parquetQuery";

const queryParquet = makeParquetQuerier("TMT-CERAD-GREFEX");

const NIVEAU_MAP = { I: 1, II: 2, III: 3 };
const PC_COLS = [
  ["pc_95", 95],
  ["pc_90", 90],
  ["pc_75", 75],
  ["pc_50", 50],
  ["pc_25", 25],
  ["pc_10", 10],
  ["pc_5", 5],
];

function getAgeGroup(age) {
  if (age < 40) return "lt40";
  if (age < 60) return "40_59";
  return "gte60";
}

/**
 * Look up a GREFEX TMT count-based PC (errors or perseverations) for a given raw count.
 * Walks PC cutoffs from 95 down to 5 and returns the first PC whose threshold the
 * count does not exceed. Returns "< 1" when the patient exceeds the PC=5 threshold
 * AND that threshold is fractional (between 0 and 1, exclusive), otherwise "< 5".
 * @param {string} filename - parquet file under src/db/TMT-CERAD-GREFEX
 * @param {number} age
 * @param {string} niveau - "I" | "II" | "III"
 * @param {string|number} value - raw count
 * @returns {Promise<{pc: string} | {error: string}>}
 */
async function lookupGrefexCountPC(filename, age, niveau, value, lang) {
  if (age == null) {
    return { error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante" };
  }

  const niveauNum = NIVEAU_MAP[niveau];
  if (!niveauNum) {
    return { error: lang === "de" ? "Bildungsniveau fehlt" : "Niveau d'études manquant" };
  }

  const n = parseFloat(value);
  if (isNaN(n)) return { error: lang === "de" ? "Ungültiger Wert" : "Valeur invalide" };

  const ageGroup = getAgeGroup(age);
  const rows = await queryParquet(filename);
  const row = rows.find((r) => r.age_group === ageGroup && r.niveau === niveauNum);
  if (!row) {
    return {
      error: lang === "de"
        ? `Keine Übereinstimmung (${ageGroup}, Niveau ${niveauNum})`
        : `Aucune correspondance (${ageGroup}, niveau ${niveauNum})`,
    };
  }

  for (const [col, pc] of PC_COLS) {
    if (n <= row[col]) return { pc: String(pc) };
  }

  const pc5 = row.pc_5;
  return { pc: pc5 > 0 && pc5 < 1 ? "< 1" : "< 5" };
}

/**
 * Look up the TMT A "Partie A erreurs" PC for a given error count.
 * @param {number} age
 * @param {string} niveau - "I" | "II" | "III"
 * @param {string|number} errors
 * @returns {Promise<{pc: string} | {error: string}>}
 */
export function calcTmtAErreursPC(age, niveau, errors, lang) {
  return lookupGrefexCountPC("tmta-erreurs.parquet", age, niveau, errors, lang);
}

/**
 * Look up the TMT B "Partie B persévérations" PC for a given perseveration count.
 * @param {number} age
 * @param {string} niveau - "I" | "II" | "III"
 * @param {string|number} perseverations
 * @returns {Promise<{pc: string} | {error: string}>}
 */
export function calcTmtBPersevPC(age, niveau, perseverations, lang) {
  return lookupGrefexCountPC("tmtb-perseverations.parquet", age, niveau, perseverations, lang);
}
